import Cards from "components/scenes/Lesson/Cards";
import Stories from "components/scenes/Stories";
import { useEffect, useState, useMemo } from "react";
import { testData } from "./utils/testData";
import { generateMockLessonData } from "./utils/mockData";
import { articles } from "./utils/articles";
import { obfuscateEmail } from "./utils/obfuscateEmail";
import { getThought } from "./utils/Language/Thoughts";
import lang from "./utils/lang";
import "./styles/index.scss";

function App() {
  const [response, setResponse] = useState(null);
  const [baseUrl, setBaseUrl] = useState("");
  const [lessonId, setLessonId] = useState(null);
  const [cardId, setCardId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const childCallingName = document
    .getElementById("root")
    .getAttribute("data-child-calling-name");

  useEffect(() => {
    // parse base url and lesson id from url
    const noTrailingSlashUrl = window?.location?.href?.replace(/\/$/, "");
    setBaseUrl(
      noTrailingSlashUrl?.replace(/\/\d+$/, "")?.replace(/\/\d+$/, "")
    );
    const splitPath = noTrailingSlashUrl?.split("/");
    const parsedLessonId = parseInt(splitPath[splitPath?.length - 2]);
    const parsedCardId = parseInt(splitPath[splitPath?.length - 1]);
    setLessonId(!isNaN(parsedLessonId) ? parsedLessonId : 1);
    setCardId(!isNaN(parsedCardId) ? parsedCardId : 1);
  }, [cardId, lessonId]);

  const thought = useMemo(() => {
    return childCallingName ? getThought(childCallingName) : "";
  }, [childCallingName]);

  useEffect(() => {
    // fetch lesson data
    if (!lessonId || lessonId < 1) return;

    setIsLoading(true);
    let timeoutId = null;

    const hosts = [
      {
        alias: "localhost",
        url: "http://localhost:8890/lessons-data",
      },
      {
        alias: "192.168",
        url: "http://192.168.2.135:8888/lessons-data",
      },
      {
        alias: "www.rebusmetod.com",
        url: "https://www.rebusmetod.com/lessons-data",
      },
      {
        alias: "beta.rebusmetod.com",
        url: "https://www.beta.rebusmetod.com/lessons-data",
      },
      {
        alias: "next.rebusmetod.com",
        url: "https://www.next.rebusmetod.com/lessons-data",
      },
    ];
    const apiUrl = hosts.filter((host) => {
      return window.location.href.includes(host?.alias);
    })[0]?.url;
    const isDevEnv = !window.location.href.includes("rebusmetod");
    
    // Enhanced security: only allow mock data in development environments
    const allowMockDataDomains = ['localhost', '127.0.0.1', '192.168'];
    const useMockData = process.env.REACT_APP_USE_MOCK_DATA === 'true' && 
                       process.env.NODE_ENV === 'development' &&
                       allowMockDataDomains.some(domain => window.location.href.includes(domain));

    async function fetchData() {
      // If mock data is enabled, use it directly
      if (useMockData) {
        const mockData = generateMockLessonData(lessonId);
        timeoutId = setTimeout(() => {
          setResponse(mockData);
          setIsLoading(false);
          isDevEnv && console.log('Using mock data:', mockData);
        }, 50 * (thought?.length || 0));
        return;
      }

      try {
        const response = await fetch(`${apiUrl}/${lessonId}/`);
        const json = await response?.json();

        // щоб можна було встигнути прочитати
        timeoutId = setTimeout(() => {
          setResponse(json);
          setIsLoading(false);
          isDevEnv && console.log(json);
        }, 50 * (thought?.length || 0));
      } catch (error) {
        setIsLoading(false);
        isDevEnv && console.warn('API Error:', error);
        
        // Fallback to mock data if API fails and we're in dev environment
        if (isDevEnv) {
          const mockData = generateMockLessonData(lessonId);
          setResponse(mockData);
          console.log('Falling back to mock data due to API error:', mockData);
        } else {
          setResponse(testData);
        }
      }
    }

    fetchData();

    // Cleanup function to clear timeout if component unmounts
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [lessonId, thought?.length]);

  useEffect(() => {
    if (response && cardId)
      document.title = `Урок ${
        response?.tasks?.id
      }, картка ${cardId}. — «У мене виходить!» | Ребус-метод — ${obfuscateEmail(
        response?.user?.email
      )}`;
  }, [cardId, lessonId, response, response?.tasks?.id, response?.user?.email]);

  return (
    <div className="main">
      {isLoading ? (
        <div className="centered">
          <img
            src="/images/beshk-11.webp"
            alt="Бешкечитун-говорун"
            className="beshkechytun"
            width={300}
            height={294}
          />
          <div>{thought}</div>
        </div>
      ) : response?.user ? (
        <>
          {response?.tasks?.id <= 48 || response?.tasks?.id >= 60 ? (
            <>
              {response?.user?.study?.currentLesson <=
              response?.user?.study?.availableLesson ? (
                <Cards
                  cardAddress={{ lessonId, cardId }}
                  lesson={response?.tasks}
                  streamlinedArticles={articles(response)}
                  baseUrl={baseUrl}
                  globals={response?.globals}
                  authUser={response?.user}
                />
              ) : (
                <div className="centered">
                  {lang?.youNeedToCompleteThePreviousLesson}
                </div>
              )}
            </>
          ) : (
            <Stories
              response={response}
              lessonId={lessonId}
              cardId={cardId}
              baseUrl={baseUrl}
            />
          )}
        </>
      ) : (
        <div className="centered">
          😛{" "}
          <a href="/login" className="anchor">
            {lang?.logIn}
          </a>{" "}
          {lang?.toYourAccount}
        </div>
      )}
    </div>
  );
}

export default App;
